/**
 * Modal "Importar CSV" — upload + preview de erros + import.
 *
 * OpenSpec add-categorized-catalog-guideline (PR 2 Fase 4).
 *
 * Fluxo:
 *  1. Admin clica "📥 Baixar template" → download .csv com headers + exemplos
 *  2. Admin edita em Excel/Sheets, salva como CSV
 *  3. Upload (file picker OU paste no textarea)
 *  4. Click "Importar" → POST /api/catalog/import/csv
 *  5. Mostra: imported N, parseErrors[], persistErrors[]
 */
import React, { useState } from 'react';
import { getCsvTemplateUrl, importCatalogCsv, CsvImportResult } from '../../services/catalog';

interface Props {
  onClose: () => void;
  onImported: () => void; // chama reload da lista no parent
}

const overlay: React.CSSProperties = {
  position: 'fixed',
  inset: 0,
  background: 'rgba(15, 23, 42, 0.6)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  zIndex: 50,
};

const modal: React.CSSProperties = {
  background: '#fff',
  borderRadius: 12,
  padding: 24,
  width: 'min(760px, 92vw)',
  maxHeight: '88vh',
  overflowY: 'auto',
  boxShadow: '0 24px 48px rgba(0,0,0,0.18)',
};

const btnPrimary: React.CSSProperties = {
  padding: '10px 18px',
  background: '#4f46e5',
  color: '#fff',
  border: 'none',
  borderRadius: 6,
  fontWeight: 600,
  cursor: 'pointer',
  fontSize: 14,
};
const btnSecondary: React.CSSProperties = {
  padding: '10px 18px',
  background: '#fff',
  color: '#475569',
  border: '1px solid #cbd5e1',
  borderRadius: 6,
  fontWeight: 600,
  cursor: 'pointer',
  fontSize: 14,
};

export const CatalogImportCsvModal: React.FC<Props> = ({ onClose, onImported }) => {
  const [csv, setCsv] = useState('');
  const [importing, setImporting] = useState(false);
  const [result, setResult] = useState<CsvImportResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      setCsv(String(reader.result || ''));
    };
    reader.readAsText(file, 'utf-8');
  };

  const handleImport = async () => {
    if (!csv.trim()) {
      setError('Cole o conteúdo do CSV ou selecione um arquivo');
      return;
    }
    setError(null);
    setImporting(true);
    try {
      const res = await importCatalogCsv(csv);
      setResult(res);
      if (res.imported > 0) {
        onImported();
      }
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setImporting(false);
    }
  };

  return (
    <div style={overlay} onClick={() => !importing && onClose()}>
      <div style={modal} onClick={(e) => e.stopPropagation()}>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: 16,
          }}
        >
          <h3 style={{ margin: 0, fontSize: 18, fontWeight: 700 }}>📥 Importar CSV</h3>
          <button
            type="button"
            onClick={() => !importing && onClose()}
            style={{ background: 'none', border: 'none', fontSize: 22, cursor: 'pointer', color: '#94a3b8' }}
            disabled={importing}
          >
            ×
          </button>
        </div>

        <div style={{ marginBottom: 16, padding: 12, background: '#eef2ff', borderRadius: 8, fontSize: 13 }}>
          <p style={{ margin: '0 0 8px 0' }}>
            <strong>Como funciona:</strong>
          </p>
          <ol style={{ margin: 0, paddingLeft: 20, lineHeight: 1.6 }}>
            <li>
              Baixe o template:{' '}
              <a
                href={getCsvTemplateUrl()}
                download="catalogo-template.csv"
                style={{ color: '#4f46e5', fontWeight: 600 }}
              >
                catalogo-template.csv
              </a>
            </li>
            <li>Edite em Excel ou Google Sheets (campos obrigatórios: name, type, categoryLabel)</li>
            <li>Salve como .csv (UTF-8) e faça upload abaixo</li>
            <li>Atributos livres: colunas começando com <code>attr_</code></li>
            <li>FAQs: pares <code>faq_1_q</code> + <code>faq_1_a</code>, <code>faq_2_q</code>...</li>
          </ol>
        </div>

        <div style={{ marginBottom: 12 }}>
          <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#475569', marginBottom: 6 }}>
            Arquivo CSV
          </label>
          <input type="file" accept=".csv,text/csv" onChange={handleFileSelect} style={{ marginBottom: 8 }} />
          <div style={{ fontSize: 11, color: '#94a3b8', marginBottom: 8 }}>
            ou cole o conteúdo direto abaixo:
          </div>
          <textarea
            value={csv}
            onChange={(e) => setCsv(e.target.value)}
            placeholder="name,type,categoryLabel&#10;..."
            style={{
              width: '100%',
              minHeight: 180,
              padding: 10,
              border: '1px solid #cbd5e1',
              borderRadius: 6,
              fontSize: 12,
              fontFamily: 'monospace',
              background: '#fff',
            }}
          />
          <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 4 }}>
            {csv.split('\n').filter((l) => l.trim() && !l.trim().startsWith('#')).length - 1} linha(s) de dados
            (cap 100 por upload)
          </div>
        </div>

        {error && (
          <div
            style={{
              padding: 10,
              background: '#fef2f2',
              color: '#b91c1c',
              borderRadius: 6,
              fontSize: 13,
              marginBottom: 12,
            }}
          >
            {error}
          </div>
        )}

        {result && (
          <div style={{ marginBottom: 12 }}>
            <div
              style={{
                padding: 12,
                background: result.imported > 0 ? '#dcfce7' : '#fef3c7',
                borderRadius: 8,
                fontSize: 13,
                marginBottom: 10,
              }}
            >
              <strong>
                {result.imported > 0 ? '✓' : '⚠'} Importados: {result.imported}/{result.totalRowsParsed}
              </strong>
              {result.cappedAt && (
                <div style={{ marginTop: 4 }}>
                  Truncado em {result.cappedAt} linhas (cap por upload).
                </div>
              )}
            </div>

            {(result.parseErrors.length > 0 || result.persistErrors.length > 0) && (
              <details open style={{ fontSize: 12 }}>
                <summary style={{ cursor: 'pointer', fontWeight: 600, color: '#b91c1c' }}>
                  {result.parseErrors.length + result.persistErrors.length} erro(s)
                </summary>
                <table style={{ width: '100%', borderCollapse: 'collapse', marginTop: 8 }}>
                  <thead>
                    <tr style={{ background: '#f1f5f9', textAlign: 'left' }}>
                      <th style={{ padding: 6 }}>Linha</th>
                      <th style={{ padding: 6 }}>Tipo</th>
                      <th style={{ padding: 6 }}>Motivo</th>
                    </tr>
                  </thead>
                  <tbody>
                    {result.parseErrors.map((e, i) => (
                      <tr key={`p-${i}`} style={{ borderTop: '1px solid #e2e8f0' }}>
                        <td style={{ padding: 6 }}>{e.row}</td>
                        <td style={{ padding: 6 }}>Parse</td>
                        <td style={{ padding: 6 }}>{e.reason}</td>
                      </tr>
                    ))}
                    {result.persistErrors.map((e, i) => (
                      <tr key={`s-${i}`} style={{ borderTop: '1px solid #e2e8f0' }}>
                        <td style={{ padding: 6 }}>{e.row}</td>
                        <td style={{ padding: 6 }}>Save</td>
                        <td style={{ padding: 6 }}>
                          {e.name}: {e.reason}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </details>
            )}
          </div>
        )}

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
          <button type="button" onClick={onClose} style={btnSecondary} disabled={importing}>
            {result ? 'Fechar' : 'Cancelar'}
          </button>
          {!result && (
            <button
              type="button"
              onClick={handleImport}
              style={{ ...btnPrimary, opacity: importing ? 0.6 : 1 }}
              disabled={importing || !csv.trim()}
            >
              {importing ? 'Importando…' : 'Importar'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default CatalogImportCsvModal;
